import { Detaljniva, DetaljnivaAdditional } from '@/middleware/queries';
import { Kontraktsomrade, Riksvegrute, Vegsystemreferanse } from '../omrader';
import { WKTObject } from '../WKTObject';
import { EgenskapsTypeEnum } from './EgenskapsTypeEnum';
import { Stedfesting } from './Stedfesting';

export class Vegobjekt {
    static dummy(
        id: number,
        typeId: number | null,
        geometri: WKTObject = null!
    ): Vegobjekt {
        return new Vegobjekt(
            id,
            null!,
            new VegobjektMetadata({ id: typeId!, navn: null! }, null!, null!, null!),
            null!,
            geometri,
            null!,
            null!
        );
    }

    constructor(
        readonly id: number,
        readonly href: string,
        readonly metadata: VegobjektMetadata,
        readonly egenskaper: VegobjektEgenskap[],
        readonly geometri: WKTObject | null,
        readonly lokasjon: VegobjektLokasjon,
        readonly relasjoner: VegobjektRelasjoner,
        readonly segmenter?: Vegsegment[]
    ) {}
}

export class VegobjektMetadata {
    constructor(
        readonly type: { id: number; navn: string },
        readonly version: number,
        readonly startdato: string,
        readonly sist_modifisert: string,
        readonly sluttdato?: string
    ) {}
}

export class VegobjektLokasjon {
    constructor(
        readonly kommuner: number[],
        readonly fylker: number[],
        readonly kontraktsområder: Kontraktsomrade[],
        readonly riksvegruter: Riksvegrute[],
        readonly vegsystemreferanser: Vegsystemreferanse[],
        readonly stedfestinger: Stedfesting[],
        readonly geometri: WKTObject | null,
        readonly strekningslengde: number = 0
    ) {}
}

export class VegobjektEgenskap {
    constructor(
        readonly id: number,
        readonly navn: string,
        readonly egenskapstype: string,
        readonly stedfestingstype: string | null = null,
        readonly verdi: string | number | boolean | null = null,
        readonly enum_id: number | null = null,
        readonly href: string | null = null,
        readonly verifiseringsdato: string | null = null,
        readonly datafangstdato: string | null = null,
        readonly kvalitet: VegobjektEgenskapKvalitetAttributt[] | null = null,
        readonly enhet: VegobjektEgenskapEnhet | null = null,
        readonly høydereferanse: number | null = null
    ) {}

    erGeometri(): boolean {
        return this.egenskapstype === EgenskapsTypeEnum.GEOMETRI;
    }

    erAssosiasjon(): boolean {
        return this.egenskapstype === EgenskapsTypeEnum.ASSOSIASJON;
    }
}

export class VegobjektEgenskapEnhet {
    constructor(
        readonly id: number,
        readonly navn: string,
        readonly kortnavn: string
    ) {}
}

export class VegobjektEgenskapKvalitetAttributt {
    public static comparator(
        a: VegobjektEgenskapKvalitetAttributt,
        b: VegobjektEgenskapKvalitetAttributt
    ): number {
        return a?.displayName?.localeCompare(b.displayName);
    }

    constructor(
        readonly name: string,
        readonly displayName: string,
        readonly value: number | string
    ) {}
}

export enum Kvalitettype {
    synbarhet = 'Synbarhet',
    nøyaktighet = 'Nøyaktighet',
    nøyaktighetHøyde = 'Nøyaktighet, høyde',
    maksimaltAvvik = 'Maksimalt avvik',
    datafangstmetode = 'Datafangstmetode',
    datafangstmetodeHøyde = 'Datafangstmetode, Høyde',
}

export enum Relasjonstype {
    Mor,
    Datter,
}

export class VegobjektRelasjoner {
    constructor(
        readonly barn: VegobjektRelasjon[],
        readonly foreldre: VegobjektRelasjon[]
    ) {}
}

export class VegobjektRelasjon {
    constructor(
        readonly type: { id: number; navn: string },
        readonly vegobjekter: Vegobjekt[],
        readonly relasjonstype: Relasjonstype
    ) {}
}

export class Vegsegment {
    constructor(
        readonly veglenkesekvensid: number,
        readonly startposisjon: number,
        readonly sluttposisjon: number,
        readonly detaljniva: Detaljniva | DetaljnivaAdditional,
        readonly geometri: WKTObject,
        readonly feltoversikt: string[]
    ) {}
}

export function vegobjektVersionsComparator(
    vegobjektA: Vegobjekt,
    vegobjektB: Vegobjekt
) {
    return vegobjektA.metadata.version - vegobjektB.metadata.version;
}

export function vegobjektStartDatesComparator(vegobjekt: Vegobjekt) {
    return new Date(vegobjekt.metadata.startdato) <= new Date();
}

export function getHoydereferanseText(num: number | null): string {
    switch (num) {
        case 0:
            return 'Fot';
        case 1:
            return 'Topp';
        case 99:
            return 'Ukjent';
        case 9999:
            return 'Ikke registrert';
        case null:
            return 'Ikke registrert';
        default:
            return 'Ugyldig verdi';
    }
}
